/**
 * Place labels over the ortho imagery (#955).
 *
 * The ortho rasters carry no place names and paint on top of `base`, the only
 * raster that has them. The `aero` overlay stops below their minimum zoom. So
 * from `ortho.layerMinZoom` up, nothing on the map names a place. This layer
 * draws the `pack-cities` places there.
 *
 * It draws ONLY where an ortho paints: at the ortho's zoom, and inside its
 * `bounds`. Outside the bounds `base` keeps painting with its own names, and a
 * label of ours beside the raster's own would show every place twice.
 *
 * The rows come from the `citiesInBounds` catalog entry, which orders the
 * biggest place first. `kind` sizes the text and the row order decides which
 * label wins a collision, so a village never displaces a city.
 */

import { Map as MlMap } from "maplibre-gl";
import { emptyFC } from "./webgl-maplibre.helpers";
import { MapConfig, RasterLayerConfig } from "./webgl-maplibre.types";

export const CITY_SOURCE_ID = "cities";
export const CITY_LAYER_ID = "city-labels";

/** `LIMIT` of the `citiesInBounds` catalog entry. A result of this length is
 *  truncated, so it is NOT a complete picture of the box that was asked for. */
export const CITY_QUERY_LIMIT = 200;

/** The box caps the `citiesInBounds` SQL enforces. An oversized box answers no
 *  rows rather than scanning the whole pack on the app-server event loop, so a
 *  padded box must stay inside these. */
export const CITY_BOX_MAX_LON = 1;
export const CITY_BOX_MAX_LAT = 0.5;

/** Viewports of padding added on EACH side, so a pan stays inside data the
 *  source already holds and needs no query. The labels are only fetched when
 *  the camera settles, so without this a pan exposes blank ground until
 *  `moveend`. */
export const CITY_PAD_VIEWPORTS = 1;

export interface CityBox {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

/** Grows `view` by `CITY_PAD_VIEWPORTS` on each side, clamped to the SQL caps
 *  and to the Mercator latitude limit. A viewport already at or over a cap is
 *  returned unpadded on that axis rather than shrunk — shrinking would ask for
 *  less than the map is showing. */
export function paddedCityBox(view: CityBox): CityBox {
  const lonSpan = view.maxX - view.minX;
  const latSpan = view.maxY - view.minY;
  const lonPad = Math.max(0, Math.min(lonSpan * CITY_PAD_VIEWPORTS, (CITY_BOX_MAX_LON - lonSpan) / 2));
  const latPad = Math.max(0, Math.min(latSpan * CITY_PAD_VIEWPORTS, (CITY_BOX_MAX_LAT - latSpan) / 2));
  return {
    minX: view.minX - lonPad,
    maxX: view.maxX + lonPad,
    minY: Math.max(-85.051129, view.minY - latPad),
    maxY: Math.min(85.051129, view.maxY + latPad),
  };
}

/** True when `inner` lies wholly inside `outer`, so the source already holds
 *  every label for it. */
export function boxContains(outer: CityBox, inner: CityBox): boolean {
  return outer.minX <= inner.minX && outer.maxX >= inner.maxX && outer.minY <= inner.minY && outer.maxY >= inner.maxY;
}

/** Raster keys that are aerial imagery, in `MapConfig`. */
const ORTHO_KEYS = ["ortho", "orthoBy"] as const;

/** A row of the `citiesInBounds` catalog entry. */
export interface CityRow {
  id: number;
  name: string;
  kind: string;
  population: number;
  latitude: number;
  longitude: number;
}

export interface OrthoCoverage {
  /** Lowest zoom at which any ortho paints. */
  minZoom: number;
  /** One `[w, s, e, n]` box per ortho, or null when an ortho has no bounds. */
  boxes: [number, number, number, number][] | null;
}

/** Where the ortho imagery paints, or null when no ortho is configured.
 *
 *  ponytail: one minZoom for both orthos, not a zoom per box. Both carry 15
 *  today; split it per box if one ortho ever starts at a different zoom. */
export function orthoCoverage(mapConfig: MapConfig): OrthoCoverage | null {
  const layers = ORTHO_KEYS.map((key) => mapConfig[key]).filter((cfg): cfg is RasterLayerConfig => !!cfg?.url);
  if (!layers.length) return null;
  const boxes = layers.map((l) => l.bounds);
  return {
    minZoom: Math.min(...layers.map((l) => l.layerMinZoom)),
    // An ortho without bounds covers the whole world, so nothing is outside.
    boxes: boxes.every((b) => !!b) ? (boxes as [number, number, number, number][]) : null,
  };
}

export function inCoverage(coverage: OrthoCoverage, lat: number, lon: number): boolean {
  if (!coverage.boxes) return true;
  return coverage.boxes.some(([w, s, e, n]) => lon >= w && lon <= e && lat >= s && lat <= n);
}

export function cityLabelFeatures(rows: CityRow[], coverage: OrthoCoverage): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  for (const row of rows) {
    const lat = Number(row.latitude);
    const lon = Number(row.longitude);
    if (!isFinite(lat) || !isFinite(lon)) continue;
    if (!inCoverage(coverage, lat, lon)) continue;
    features.push({
      type: "Feature",
      id: row.id,
      // The query returns the biggest place first, so the position in the list
      // IS the label priority: MapLibre places a lower `symbol-sort-key` first,
      // and with `text-allow-overlap: false` the first one placed keeps the spot.
      properties: { name: row.name, kind: row.kind, rank: features.length },
      geometry: { type: "Point", coordinates: [lon, lat] },
    });
  }
  return { type: "FeatureCollection", features };
}

/** Adds the source and the label layer. Call it straight after the rasters, so
 *  the labels sit above the imagery and below every other layer — they must
 *  never cover the route, an obstacle, an icon or an aeronautical label. */
export function addCityLabelLayer(map: MlMap, minZoom: number): void {
  map.addSource(CITY_SOURCE_ID, { type: "geojson", data: emptyFC() });
  map.addLayer({
    id: CITY_LAYER_ID,
    type: "symbol",
    source: CITY_SOURCE_ID,
    minzoom: minZoom,
    layout: {
      "text-field": ["get", "name"],
      "text-font": ["Open Sans Semibold"],
      "text-size": ["match", ["get", "kind"], "city", 48, "town", 40, 32],
      "text-anchor": "center",
      "text-padding": 4,
      "text-allow-overlap": false,
      "symbol-sort-key": ["get", "rank"],
    },
    paint: {
      "text-color": "#1a1a1a",
      // Aerial imagery is busy and of any brightness, so the halo carries the
      // contrast rather than the text colour.
      "text-halo-color": "#ffffff",
      "text-halo-width": 3,
      // Slightly transparent so a big label reads as an underlay: the imagery
      // and anything drawn above it stay legible through the text. 0.5 was too
      // faint at this glyph size (Igor, 2026-09-18).
      "text-opacity": 0.8,
    },
  });
}
